/*
 * SS06238 - report/document downloads that named many entries or carried many filters did nothing.
 *
 * The document services take the document model on the query string:
 *   GET /service/pdftemplateprocessing/document?documentmodel=<encoded JSON>
 * and the model carries the listing's filters (default filters included) and the selected entry
 * ids. Past IIS's 2048-byte query-string limit the server rejects the CORS preflight with a 404
 * that has no Allow-Origin, so the browser reports a CORS error and nothing downloads.
 *
 * The same URL also takes POST with the model as a JSON body (ServiceController.PostDocument) and
 * answers with the same file. Ruling (2026-10-05): keep GET by default, switch to POST only when
 * the GET would be too long - so a short request stays byte-identical to what it always was.
 */
export const PDF_DOCUMENT_PATH = "/service/pdftemplateprocessing/document";
export const HTML_DOCUMENT_PATH = "/service/htmltemplateprocessing/document";

/** Longest `documentmodel=...` query string sent as a GET (IIS default maxQueryString is 2048). */
export const DOCUMENT_QUERY_LIMIT = 2000;

/** Content-Type of the POST body, as FTSHtmlViewer's Excel export already sends it. */
export const DOCUMENT_POST_CONTENT_TYPE = "application/json;charset=UTF-8";

/**
 * SS05651 - a document would not open once the access token had expired: "Error, Unauthorized", and
 * again on every retry, until the whole page was reloaded.
 *
 * The document services are asked with a raw XMLHttpRequest (the answer is a file), outside the
 * shared HTTP client, so the client's "401 -> refresh the token -> send again" never ran for them.
 * This does the same for such a request: `send()` performs it and answers something with a
 * `status`; on a 401 the token is refreshed once (`refreshToken()`) and the request is sent again,
 * whatever that second answer is. A refresh that fails leaves the 401 as the answer: the refresh
 * itself signs the person out when the session cannot be renewed.
 */
export async function sendWithTokenRefresh(send, refreshToken) {
  const first = await send();
  if (!first || first.status !== 401) return first;
  try {
    await refreshToken();
  } catch (err) {
    return first;
  }
  return send();
}

/**
 * sendWithTokenRefresh for a request answered through a callback (an XMLHttpRequest's onload) rather
 * than a promise: `send(answer)` performs the request and calls `answer(res)` with something that has
 * a `status`; `done(res)` is called once, with the answer to act on. Any answer but a 401 reaches
 * `done` at once, within the same callback; a 401 is renewed and sent again exactly as above.
 */
export function sendWithTokenRefreshCallback(send, refreshToken, done) {
  send((first) => {
    if (!first || first.status !== 401) {
      done(first);
      return;
    }
    Promise.resolve()
      .then(refreshToken)
      .then(() => send(done), () => done(first));
  });
}

/**
 * How to ask `path` for the document described by `documentModel` (an object or its JSON string).
 * Returns `{ method: "GET", url }` while the query string fits, else `{ method: "POST", url, body }`
 * where `body` is the model's JSON.
 */
export function buildDocumentRequest(path, documentModel) {
  const json = typeof documentModel === "string" ? documentModel : JSON.stringify(documentModel);
  // encodeURIComponent only emits ASCII, so the string length is the byte length on the wire.
  const query = "documentmodel=" + encodeURIComponent(json);
  if (query.length <= DOCUMENT_QUERY_LIMIT) {
    return { method: "GET", url: path + "?" + query };
  }
  return { method: "POST", url: path, body: json };
}
